import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import cls from 'classnames';
import { useHover } from 'ahooks';
import { Bubble, Segment } from 'tea-component';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import SingleTranslateSelect from '@op/components/FilterBoxCommon/filters/languages/singleLauage';
import Loading from '@/pages/intelligence/components/Loading';
import { getSteamNewAndAnnouncements } from '@intelligence/services/topcharts';
import SteamNewsItem from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/SteamNewsAndAnnouncements/components/SteamNewsItem';
import OperationIcon from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/components/OperationIcon';
import useLocaleSegmentOptions from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/SteamNewsAndAnnouncements/hooks/useLocaleSegmentOptions';
import { downloadAction } from '@intelligence/components/DownloadCommon/DownloadPage';
import steamIcon from '@intelligence/assets/svg/dataSource/steam.svg';
import {
  getSteamNewsJumpLink,
  getLocaleList,
  getType,
  specifySubtitleColor,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/SteamNewsAndAnnouncements/_utils';
import { useLoadMore } from './hooks/useLoadMore';
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint';
import { dbiBtnLogMes } from '@/pages/intelligence/utils/buriedPointCfg';

import './index.scss';

const pageSize = 5;

interface ISteamNewsAndAnnouncements {
  gameId: string;
  gameName: string;
  steamID: string;
  dLoading: boolean;
}

export interface INewsDataLocaleItem {
  start_time: string;
  cover: string;
  url: string;
  title: string;
  subTitle: string;
  subTitleColor: string;
  desc: string;
}

type TSelectLang = 'origin' | 'en' | 'zh';

function SteamNewsAndAnnouncements({
  gameId,
  gameName,
  steamID,
  dLoading,
}: ISteamNewsAndAnnouncements) {
  const { formatMessage, isCN } = useIntlExtend();

  const jumpUrl = getSteamNewsJumpLink(steamID);

  const [currentSegment, setCurrentSegment] = useState('all');
  const [newsData, setNewsData] = useState<{ [key: string]: INewsDataLocaleItem[] }>({});
  const hasMoreRef = useRef(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchMoreLoading, setFetchMoreLoading] = useState(false);

  const [selectedLang, setSelectedLang] = useState<TSelectLang>(isCN ? 'zh' : 'en');

  const colorMapRef = useRef({});

  const ref = useRef(null);
  const isHovering = useHover(ref);

  // 根据当前语言更新选中的语言框
  useEffect(() => {
    setSelectedLang(isCN ? 'zh' : 'en');
  }, [isCN]);

  // 获取 Segment 选项，及选中 others 时需排除的 eventType
  const { localeSegmentOptions, excludeEventType } = useLocaleSegmentOptions(isCN);

  interface IGetSteamNewDataProps {
    isAll?: boolean;
    isOthers?: boolean;
    eventType?: string;
    isFetchMore?: boolean;
  }

  const getSteamNewData = async ({
    isAll,
    isOthers,
    eventType,
    isFetchMore,
  }: IGetSteamNewDataProps) => {
    isFetchMore ? setFetchMoreLoading(true) : setFetchLoading(true);

    // 当前查询 segment 类型
    const type = getType({ isAll, isOthers, eventType });
    const nextPage = newsData[type]?.length / pageSize + 1;
    const page = isFetchMore ? nextPage : 1;

    // 异常触发不处理
    if (!page || String(page).includes('.')) return;

    const params = {
      id: gameId,
      page,
      page_size: pageSize,
      b_others: isOthers,
      event_types: (() => {
        if (isAll) return [];
        if (isOthers) return excludeEventType || [];
        return [eventType];
      })(),
      sources: ['steam'],
      entity_type: 'combine',
    };

    try {
      const res = await getSteamNewAndAnnouncements(params);
      const { game_events: gameEvents, has_more: hasMore } = res || {};

      // 当数据少于请求条数，没有更多数据
      hasMoreRef.current = hasMore;
      // 当前语言处理展示
      const list = getLocaleList(gameEvents, selectedLang);

      // 给副标题指定颜色
      specifySubtitleColor(list, colorMapRef);

      // 当前查询 segment 类型
      // const type = getType({ isAll, isOthers, eventType });

      setNewsData((prev: any) => {
        const currentData = isFetchMore
          ? { [type]: [...(prev[type] || []), ...(list || [])] }
          : { [type]: list };
        return { ...prev, ...currentData };
      });
    } catch (error) {
      hasMoreRef.current = false;
    }
    setFetchMoreLoading(false);
    setFetchLoading(false);
  };

  // 切换 segment
  const handleSegmentTypeChange = (value: string) => {
    hasMoreRef.current = false;

    if (value === 'all') {
      getSteamNewData({ isAll: true });
    } else if (value === 'others') {
      getSteamNewData({ isOthers: true });
    } else {
      getSteamNewData({ eventType: value });
    }

    setCurrentSegment(value);
  };

  // 跳转列表
  const handleJump = () => {
    window.open(jumpUrl);
  };

  const handleDownloadLog = (logConfigKey?: string) => {
    // @ts-ignore
    const logConfig = dbiBtnLogMes[logConfigKey];
    if (!logConfig) return;
    buriedBtnPoint(logConfig);
  };

  // 点击下载
  const handleDownload = () => {
    const fileName = `${gameName}_Steam_Events & Announcements_${moment().format('YYYY-MM-DD')}`;
    handleDownloadLog('TopChartsPCAndConsoleGamesGameInfoSteamNewsAndAnnouncementsDownloadButton');
    downloadAction({
      downLoadParams: { fileName, id: gameId, file_format: 0 },
      downloadKey: 'steamNews',
    });
  };

  // 语言框切换更新当前语言数据
  useEffect(() => {
    setNewsData((prev) => {
      const currentList = prev?.[currentSegment]?.slice();
      const localeList = getLocaleList(currentList, selectedLang);

      return {
        ...prev,
        [currentSegment]: localeList,
      };
    });
  }, [selectedLang, currentSegment]);

  // 初始请求数据
  useEffect(() => {
    getSteamNewData({ isAll: true });
  }, []);

  // 滚动加载更多
  useLoadMore({ newsData, currentSegment, hasMoreRef, getSteamNewData });

  return (
    <div className={cls('dbi-steam-news-container', { 'has-more': hasMoreRef.current })} ref={ref}>
      <div className="dbi-steam-news-title">
        <div className="dbi-steam-news-title-left">
          <img src={steamIcon} alt="" />
          <div className="dbi_tag_card_title-text">
            {formatMessage({ id: 'dbi_steam_news_announcements' })}
          </div>
        </div>
        {isHovering && (
          <div className="dbi-steam-news-title-right">
            {jumpUrl && (
              <Bubble
                positionFixed={false}
                overlayClassName={'dbi-steam-news-jump-icon-bubble'}
                content={formatMessage({ id: 'dbi_top_steam_news_jump_tip' })}
              >
                <div>
                  <OperationIcon type="jump" onClick={handleJump} />
                </div>
              </Bubble>
            )}
            <OperationIcon type="download" onClick={handleDownload} />
          </div>
        )}
      </div>

      <div className="dbi-steam-news-content">
        <div className="dbi-steam-news-content-operation">
          <Segment
            value={currentSegment}
            options={localeSegmentOptions}
            onChange={handleSegmentTypeChange}
          />

          <SingleTranslateSelect
            defaultValue={selectedLang}
            onChange={(_: any, value: TSelectLang) => {
              setSelectedLang(value);
            }}
          />
        </div>

        {!fetchLoading && (
          <div className="dbi-steam-news-list">
            {newsData?.[currentSegment]?.map((item, index: number) => {
              return (
                <SteamNewsItem
                  key={currentSegment + index}
                  data={item}
                  isFirst={!index}
                  isLast={index === newsData?.[currentSegment]?.length - 1}
                />
              );
            })}

            {/* 加载更多的触发器 */}
            {Boolean(newsData?.[currentSegment]?.length) && !dLoading && (
              <div className="load-more-trigger" style={{ height: '1px' }}></div>
            )}

            {/* 无数据 */}
            {!newsData?.[currentSegment]?.length && (
              <div className="dbi-steam-news-no-data">{formatMessage({ id: 'dbi_no_data' })}</div>
            )}
          </div>
        )}

        {/* 加载中 */}
        {fetchLoading && (
          <div className="dbi-steam-news-loading">
            <Loading />
          </div>
        )}

        {/* 加载更多 */}
        {fetchMoreLoading && !fetchLoading && (
          <div className="dbi-steam-news-fetch-more-loading">
            <Loading />
          </div>
        )}
      </div>
    </div>
  );
}

export default SteamNewsAndAnnouncements;
